import { motion } from "framer-motion";

const STEPS = [
    {
        n: "01",
        title: "Establish Profile",
        copy: "Share your context in a confidential two-minute brief. We run a soft-check that never touches your bureau score.",
        color: "gold",
        img: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=700&q=80",
    },
    {
        n: "02",
        title: "Curate Documents",
        copy: "Upload KYC, statements and proof of income through our encrypted vault. Your advisor packages the file personally.",
        color: "forest",
        img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=700&q=80",
    },
    {
        n: "03",
        title: "Receive Capital",
        copy: "A bespoke offer is presented within hours. Funds settle to your account in as little as 24 hours.",
        color: "coral",
        img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=700&q=80",
    },
];

const TEXT = { gold: "text-gold", forest: "text-forest", coral: "text-coral" };
const BAR = { gold: "bg-gold", forest: "bg-forest", coral: "bg-coral" };
const BADGE = {
    gold: "bg-gold/10 text-gold border-gold/30",
    forest: "bg-forest/10 text-forest border-forest/30",
    coral: "bg-coral/10 text-coral border-coral/30",
};

export default function HowItWorks() {
    return (
        <section
            id="process"
            data-testid="process-section"
            className="relative py-20 md:py-28 bg-cream-warm overflow-hidden"
        >
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-1/4 w-80 h-80 bg-coral/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-forest/10 rounded-full blur-3xl" />
            </div>

            <div className="relative max-w-[1280px] mx-auto px-6 md:px-10">
                <div className="text-center mb-14">
                    <div className="flex items-center justify-center gap-3 mb-5">
                        <span className="w-8 h-px bg-gold" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                            Three Honest Steps
                        </span>
                        <span className="w-8 h-px bg-gold" />
                    </div>
                    <h2 className="font-serif text-4xl md:text-5xl leading-[1] tracking-tight font-medium text-ink max-w-2xl mx-auto">
                        From conversation<br />
                        to <em className="italic text-gold font-normal">capital</em> — quietly.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {STEPS.map((s, i) => (
                        <motion.div
                            key={s.n}
                            data-testid={`process-step-${s.n}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.15 }}
                            className="relative bg-white border border-cream-deep group hover:shadow-2xl hover:-translate-y-1 transition-all overflow-hidden"
                        >
                            <div className={`absolute top-0 left-0 right-0 h-1 ${BAR[s.color]} z-10`} />
                            <div className="relative h-40 overflow-hidden">
                                <img
                                    src={s.img}
                                    alt={s.title}
                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-white/40 to-transparent" />
                                <div className={`absolute top-4 left-4 px-2 py-1 border font-mono text-[10px] uppercase tracking-[0.25em] ${BADGE[s.color]} bg-white/95 backdrop-blur`}>
                                    Step {s.n}
                                </div>
                            </div>
                            <div className="p-7">
                                <div className={`font-serif text-5xl ${TEXT[s.color]} leading-none mb-3`}>
                                    {s.n}
                                </div>
                                <h3 className="font-serif text-2xl text-ink mb-2">{s.title}</h3>
                                <p className="text-ink-muted font-light leading-relaxed text-base">
                                    {s.copy}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
